// src/components/Admin/ManageColleges.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ This is your configured axios instance
import { useDropzone } from 'react-dropzone';

export default function ManageColleges() {
  const [colleges, setColleges] = useState([]);
  const [form, setForm] = useState({ name: '', acronym: '', logo_url: '', team_number: '', id: '' });
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      const res = await api.get('/colleges');
      setColleges(res.data);
      setMessage('');
    } catch (err) {
      console.error("Failed to fetch colleges", err);
      setMessage('Failed to load colleges');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // ✅ Use 'api' instance
      await api.post('/colleges', form);
      setForm({ name: '', acronym: '', logo_url: '', team_number: '', id: '' });
      fetchColleges();
      setMessage('✅ College added successfully!');
    } catch (err) {
      setMessage('❌ Failed to add college: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUpdate = async (id) => {
    try {
      // ✅ Use 'api' instance
      await api.put(`/colleges/${id}`, colleges.find(c => c.id === id));
      fetchColleges();
      setMessage('✅ College updated!');
    } catch (err) {
      setMessage('❌ Update failed: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleInputChange = (id, field, value) => {
    setColleges(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this college?')) {
      try {
        // ✅ Use 'api' instance
        await api.delete(`/colleges/${id}`);
        fetchColleges();
        setMessage('🗑️ College deleted successfully!');
      } catch (err) {
        setMessage('❌ Failed to delete college: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  // File Upload Logic
  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0 || !form.id) return;

    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append('logo', file);

    try {
      // ✅ Use 'api' instance
      const response = await api.post(`/colleges/upload-logo/${form.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setColleges(prevColleges =>
        prevColleges.map(college =>
          college.id === form.id ? { ...college, logo_url: response.data.college.logo_url } : college
        )
      );

      setMessage('✅ Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage('❌ Upload failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    onDrop // ✅ Correctly pass the onDrop function
  });

  return (
    <div>
      <h2 className="text-xl font-bold mb-6">Manage Colleges</h2>

      {/* Success/Error Message */}
      {message && (
        <div className={`p-3 mb-4 rounded text-white ${
          message.startsWith('✅') || message.startsWith('🗑️') ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {message}
        </div>
      )}

      {/* Add New */}
      <form onSubmit={handleSubmit} className="bg-gray-50 p-4 rounded mb-8">
        <h3 className="font-semibold mb-3">Add New College</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <input
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
            className="p-2 border rounded"
          />
          <input
            name="acronym"
            placeholder="Acronym"
            value={form.acronym}
            onChange={handleChange}
            className="p-2 border rounded"
          />

            {/* ✅ ADD THE SELECT DROPDOWN HERE */}
          <select
            value={form.id}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            className="p-2 border rounded"
          >
            <option value="">Select College for Upload</option>
            {colleges.map(c => (
              <option key={c.id} value={c.id}>{c.acronym}</option>
            ))}
          </select>


          <div {...getRootProps()} className="border-dashed border-2 border-gray-300 p-4 rounded cursor-pointer text-center">
            <input {...getInputProps()} />
            {isDragActive ? (
              <p className="text-green-600">Drop the logo here...</p>
            ) : (
              <p>Drag & drop logo, or click to select</p>
            )}
          </div>
          <input
            name="team_number"
            type="number"
            placeholder="Team #"
            value={form.team_number}
            onChange={handleChange}
            className="p-2 border rounded text-sm"
          />
          <button type="submit" className="btn btn-primary">Add</button>
        </div>
      </form>

      {/* List */}
      <div className="space-y-3">
        {colleges.map(college => (
          <div key={college.id} className="flex flex-wrap items-center gap-3 p-3 border rounded bg-white">

            <img
              src={college.logo_url ? college.logo_url : '/placeholder.png'}
              alt={college.name}
              className="w-10 h-10 rounded object-cover"
              onError={(e) => {
                e.target.src = '/placeholder.png';
                e.target.onerror = null;
              }}
            />

            <input
              value={college.name}
              onChange={(e) => handleInputChange(college.id, 'name', e.target.value)}
              className="flex-1 p-1 border rounded text-sm"
            />
            <input
              value={college.acronym}
              onChange={(e) => handleInputChange(college.id, 'acronym', e.target.value)}
              className="w-20 p-1 border rounded text-sm"
            />
            <input
              value={college.team_number}
              type="number"
              onChange={(e) => handleInputChange(college.id, 'team_number', e.target.value)}
              className="w-16 p-1 border rounded text-sm"
            />
            <button
              onClick={() => {
                setForm({ ...form, id: college.id });
              }}
              className="btn btn-secondary text-xs px-3 py-1"
            >
              Select for Upload
            </button>
            <button
              onClick={() => handleUpdate(college.id)}
              className="btn btn-primary text-xs px-3 py-1"
            >
              Save
            </button>
            <button
              onClick={() => handleDelete(college.id)}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}